import styled from '@emotion/styled'
import {
    ApiContentSection,
    ApiSectionType,
    buildCroppedImageUrl,
    INSPECT_ATTR,
} from '@storefronts/common-types'
import { useKeenSlider } from 'keen-slider/react'
import Image from 'next/image'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { useElementWidth } from '../../../hooks/useInView'
import { useMinWidth } from '../../../hooks/useMinWidth'
import {
    ImageBackdrop,
    singleImageBandStyles,
    SINGLE_IMAGE_MAX_WIDTH,
    useImageKind,
    type MaybeImageKind,
} from '../../shared/ImageBackdrop'
import { SectionContainer, SectionTitle } from './Shared'
import {
    inScrollingDesktop,
    inScrollingLayout,
    pageBleed,
    pageBleedTrack,
    scaledFontSize,
    ScrollingLayout,
    sectionMedia,
} from '../../../style/scrollingLayout'
import { Constants } from '../../../style/styleConstants'
import {
    CustomizationContext,
    MEDIA_BORDER_RADIUS_VAR,
    PRIMARY_FONT_CSS_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'

const SlideEmotion = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
})

const Slide: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <SlideEmotion className="keen-slider__slide">{children}</SlideEmotion>
    )
}

const FILMSTRIP_MIN_TRACK_WIDTH = Math.round(
    ScrollingLayout.PROSE_MAX_WIDTH * 1.5,
)
const IMAGE_GAP_PX = 8

const MAX_IMAGE_HEIGHT = 500

const FILMSTRIP_IMAGE_SIZES = `(max-width: ${FILMSTRIP_MIN_TRACK_WIDTH}px) 100vw, ${ScrollingLayout.PROSE_MAX_WIDTH}px`
const SINGLE_IMAGE_SIZES = `(max-width: ${Constants.EXPANDED_BREAKPOINT}px) 100vw, ${SINGLE_IMAGE_MAX_WIDTH}px`

/** Widest a strip image gets: the height cap at a panoramic 2:1. */
const STRIP_IMAGE_SIZES = `${MAX_IMAGE_HEIGHT * 2}px`

const StripTrack = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    gap: IMAGE_GAP_PX,
    overflowX: 'auto',
    // `overflow-x: auto` alone computes the other axis to `auto` too, which
    // leaves the strip able to scroll vertically whenever a slide overshoots.
    overflowY: 'hidden',
    scrollSnapType: 'x proximity',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    ...pageBleedTrack,
})

const StripItem = styled.div({
    flex: '0 0 auto',
    scrollSnapAlign: 'start',
    // The prose measure, or the section's own content width where that is
    // narrower. The track bleeds past the content edge and pads back to it, so
    // its content box is that width: without the `100%` an item inside a
    // product modal runs the measure and overhangs the panel.
    maxWidth: `min(${ScrollingLayout.PROSE_MAX_WIDTH}px, 100%)`,
    img: {
        maxHeight: MAX_IMAGE_HEIGHT,
        height: 'auto',
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
        borderRadius: '0.2rem',
        display: 'block',
    },
})

const bleedToPageEdges = {
    width: '100vw',
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
}

const CarouselWrapper = styled.div<{ fullBleed: boolean; capped?: boolean }>(
    ({ fullBleed, capped }) => ({
        width: '100%',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        ...(fullBleed ? bleedToPageEdges : null),
        // 100vw / fixed desktop width don't survive a fluid column.
        // One container query: a second `inScrollingLayout` spread would replace it.
        ...inScrollingLayout({
            ...(fullBleed
                ? {
                      ...pageBleed,
                      left: 'auto',
                      right: 'auto',
                  }
                : null),
            [ScrollingLayout.DESKTOP_MEDIA]: capped
                ? // A lone image casts a shadow onto the band around it, which the
                  // clip this wrapper keeps for the bleeding layouts would cut off.
                  { overflow: 'visible' }
                : // Width alone would let a portrait image run most of a wide
                  // page's height. Desktop only: on a phone the image is meant to
                  // fill the screen's width and the cap would letterbox a tall one.
                  // `contain` keeps it fitted, since the slide sets its width
                  // inline and would otherwise squash it into the height.
                  {
                      img: {
                          maxHeight: MAX_IMAGE_HEIGHT,
                          objectFit: 'contain',
                      },
                  },
        }),
        ...(capped
            ? {
                  [sectionMedia(ScrollingLayout.PROSE_MAX_WIDTH)]: {
                      width: '100%',
                      marginInline: 0,
                  },
              }
            : null),
    }),
)

const MeasureBlock = styled.div({
    [sectionMedia(ScrollingLayout.PROSE_MAX_WIDTH)]: {
        maxWidth: ScrollingLayout.PROSE_MAX_WIDTH,
        marginInline: 'auto',
    },
})

const CardContentDiv = styled.div({
    ...getTypographyRoleStyle('bodyText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 200,
        lineHeight: '1.5em',
    }),
    fontSize: scaledFontSize(14),
    marginTop: 10,
})

const CardContent: React.FC<{ content: string }> = ({ content }) => {
    return <CardContentDiv>{content}</CardContentDiv>
}

const ImageContainer = styled.div({
    position: 'relative',
    width: '100%',
})

type CarouselSection = Extract<
    ApiContentSection,
    { type: ApiSectionType.CAROUSEL }
>

type CarouselItemImage = NonNullable<CarouselSection['items'][number]['image']>

/**
 * A lone image is shown small and centred, leaving the page either side of it
 * bare; the band carries the image's own colours across that width. Inert
 * anywhere the band would be no wider than the image.
 */
const ImageBand = styled.div<{ kind: MaybeImageKind }>(({ kind }) =>
    inScrollingDesktop({
        ...pageBleed,
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        ...singleImageBandStyles(kind),
    }),
)

const CarouselImage: React.FC<{
    image: CarouselItemImage
    itemIndex: number
    sizes: string
    isFirstSlide?: boolean
    prioritizeFirstSlide?: boolean
}> = ({
    image,
    itemIndex,
    sizes,
    isFirstSlide = false,
    prioritizeFirstSlide = true,
}) => {
    const src = buildCroppedImageUrl(image.url, image.crop)
    const eagerHero = isFirstSlide && prioritizeFirstSlide
    const itemIndexAttr = {
        [INSPECT_ATTR.CAROUSEL_ITEM_INDEX]: String(itemIndex),
    }

    if (image.size === 'AUTO') {
        return (
            <Image
                priority={eagerHero}
                fetchPriority={eagerHero ? 'high' : 'low'}
                loading={eagerHero ? 'eager' : 'lazy'}
                src={src}
                style={{
                    borderRadius: `var(${MEDIA_BORDER_RADIUS_VAR})`,
                    width: 'auto',
                }}
                alt=""
                height={200}
                width={200}
                sizes={sizes}
                {...itemIndexAttr}
            />
        )
    }
    return (
        <Image
            priority={eagerHero}
            fetchPriority={eagerHero ? 'high' : 'low'}
            loading={eagerHero ? 'eager' : 'lazy'}
            src={src}
            width={0}
            height={0}
            sizes={sizes}
            style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: `var(${MEDIA_BORDER_RADIUS_VAR})`,
            }}
            alt=""
            {...itemIndexAttr}
        />
    )
}

const CarouselTextContent: React.FC<{
    title?: string
    subtitle?: string
    content?: string
}> = ({ title, subtitle, content }) => {
    if (!title && !subtitle && !content) return null

    return (
        <>
            {title && (
                <h3
                    style={{
                        ...getTypographyRoleStyle('featureTitle', {
                            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                            fontWeight: 300,
                        }),
                        fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
                        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                        marginTop: 20,
                    }}
                >
                    {title}
                </h3>
            )}
            {subtitle && (
                <h3
                    style={{
                        ...getTypographyRoleStyle('bodyText', {
                            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                            fontWeight: 300,
                        }),
                        fontSize: scaledFontSize(14),
                        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                        marginTop: 10,
                    }}
                >
                    {subtitle}
                </h3>
            )}
            {content && <CardContent content={content} />}
        </>
    )
}

const SlideContainer = styled.div({
    width: '100%',
})

const DotsContainer = styled.div({
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    pointerEvents: 'none',
})

const DotsInnerRow = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    pointerEvents: 'auto',
})

const Dot = styled.button<{ active: boolean }>(({ active }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
    cursor: 'pointer',
    transition: 'transform 0.18s ease-out, background-color 0.18s ease-out',
    transform: active ? 'scale(1.1)' : 'scale(1)',
    padding: 0,
}))

export const MultiImage: React.FC<{
    section: CarouselSection
    prioritizeImages?: boolean
}> = ({ section, prioritizeImages = true }) => {
    const wide = useMinWidth(Constants.EXPANDED_BREAKPOINT)

    if (section.type !== ApiSectionType.CAROUSEL) return null

    return wide ? (
        <ImageStrip section={section} prioritizeImages={prioritizeImages} />
    ) : (
        <SlidingCarousel
            section={section}
            prioritizeImages={prioritizeImages}
        />
    )
}

/**
 * Every image side by side and scrolled through, rather than one at a time
 * behind arrows: each keeps its own proportions and its own caption, which a
 * single shared caption slot could not do.
 */
const ImageStrip: React.FC<{
    section: CarouselSection
    prioritizeImages?: boolean
}> = ({ section, prioritizeImages = true }) => {
    const { Analytics } = useContext(AnalyticsContext)
    const trackRef = useRef<HTMLDivElement>(null)
    const lastIndexRef = useRef(0)

    const onScroll = () => {
        const track = trackRef.current
        if (!track) return
        const index = Math.round(
            (track.scrollLeft / Math.max(track.scrollWidth, 1)) *
                section.items.length,
        )
        if (index === lastIndexRef.current) return
        lastIndexRef.current = index
        Analytics.TRACK_STATIC_CAROUSEL_SWIPE(section.sectionTitle)
    }

    return (
        <StripTrack ref={trackRef} onScroll={onScroll}>
            {section.items.map((item, idx) => (
                <StripItem key={idx}>
                    {item.image && (
                        <CarouselImage
                            image={item.image}
                            itemIndex={idx}
                            sizes={STRIP_IMAGE_SIZES}
                            isFirstSlide={idx === 0}
                            prioritizeFirstSlide={prioritizeImages}
                        />
                    )}
                    <CarouselTextContent
                        title={item.title}
                        subtitle={item.subtitle}
                        content={item.content}
                    />
                </StripItem>
            ))}
        </StripTrack>
    )
}

const SlidingCarousel: React.FC<{
    section: CarouselSection
    prioritizeImages?: boolean
}> = ({ section, prioritizeImages = true }) => {
    const [currentSlide, setCurrentSlide] = useState(0)
    const { Analytics } = useContext(AnalyticsContext)
    const trackRef = useRef<HTMLDivElement>(null)
    const trackWidth = useElementWidth(trackRef)
    const isFilmstrip = trackWidth >= FILMSTRIP_MIN_TRACK_WIDTH

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        loop: true,
        initial: 0,
        drag: true,
        dragSpeed: 0.8,
        renderMode: 'performance',
        slides: {
            perView: isFilmstrip
                ? trackWidth / ScrollingLayout.PROSE_MAX_WIDTH
                : 1,
            spacing: isFilmstrip ? IMAGE_GAP_PX : 0,
            origin: isFilmstrip ? 'center' : 'auto',
        },
        slideChanged(slider) {
            Analytics.TRACK_STATIC_CAROUSEL_SWIPE(section.sectionTitle)
            setCurrentSlide(slider.track.details.rel)
        },
    })

    // Keen doesn't remeasure when --sf-page-width lands after mount.
    /* eslint-disable react-you-might-not-need-an-effect/no-derived-state */
    useEffect(() => {
        instanceRef.current?.update()
    }, [trackWidth, instanceRef])
    /* eslint-enable react-you-might-not-need-an-effect/no-derived-state */

    const dotsArray = useMemo(() => {
        if (section.type !== ApiSectionType.CAROUSEL) return []
        if (section.items.length) {
            return [...Array(section.items.length)].map(function (_x, i) {
                return i
            })
        }
        return []
    }, [section])

    if (section.type !== ApiSectionType.CAROUSEL) return null

    const currentItem = section.items[currentSlide]

    return (
        <>
            <CarouselWrapper fullBleed ref={trackRef}>
                {/* Image carousel with dots overlay */}
                <ImageContainer>
                    <SlideContainer className="keen-slider" ref={sliderRef}>
                        {section.items.map((item, idx) => {
                            return (
                                <Slide key={idx}>
                                    {item.image && (
                                        <CarouselImage
                                            image={item.image}
                                            itemIndex={idx}
                                            sizes={FILMSTRIP_IMAGE_SIZES}
                                            isFirstSlide={idx === 0}
                                            prioritizeFirstSlide={
                                                prioritizeImages
                                            }
                                        />
                                    )}
                                </Slide>
                            )
                        })}
                    </SlideContainer>
                    {section.items.length > 1 && (
                        <DotsContainer>
                            <DotsInnerRow>
                                {dotsArray.map((idx) => {
                                    return (
                                        <Dot
                                            key={idx}
                                            active={idx === currentSlide}
                                            onClick={() => {
                                                instanceRef.current?.moveToIdx(
                                                    idx,
                                                )
                                            }}
                                            aria-label={`Go to slide ${idx + 1}`}
                                        />
                                    )
                                })}
                            </DotsInnerRow>
                        </DotsContainer>
                    )}
                </ImageContainer>
            </CarouselWrapper>
            {/* Text content for current slide - rendered outside carousel */}
            <MeasureBlock>
                <CarouselTextContent
                    title={currentItem?.title}
                    subtitle={currentItem?.subtitle}
                    content={currentItem?.content}
                />
            </MeasureBlock>
        </>
    )
}

const SingleImage: React.FC<{
    section: CarouselSection
    prioritizeImages?: boolean
}> = ({ section, prioritizeImages = true }) => {
    const { fullBleedMedia = true } = useContext(CustomizationContext)
    const item = section.items[0]
    const imageUrl = item.image
        ? buildCroppedImageUrl(item.image.url, item.image.crop)
        : null
    const kind = useImageKind(imageUrl)

    return (
        <>
            <ImageBand kind={kind}>
                {imageUrl && <ImageBackdrop imageUrl={imageUrl} kind={kind} />}
                <MeasureBlock>
                    <CarouselWrapper fullBleed={fullBleedMedia} capped>
                        {item.image && (
                            <CarouselImage
                                image={item.image}
                                itemIndex={0}
                                sizes={SINGLE_IMAGE_SIZES}
                                isFirstSlide={true}
                                prioritizeFirstSlide={prioritizeImages}
                            />
                        )}
                    </CarouselWrapper>
                </MeasureBlock>
            </ImageBand>
            <MeasureBlock>
                <CarouselTextContent
                    title={item.title}
                    subtitle={item.subtitle}
                    content={item.content}
                />
            </MeasureBlock>
        </>
    )
}

export const StaticCarousel: React.FC<{
    section: CarouselSection
    prioritizeImages?: boolean
}> = ({ section, prioritizeImages = true }) => {
    return (
        <SectionContainer verticalPadding={0}>
            {section.sectionTitle && (
                <SectionTitle>{section.sectionTitle}</SectionTitle>
            )}
            {/* Mid-generation previews can render before items attach. */}
            {(section.items ?? []).length > 1 ? (
                <MultiImage
                    section={section}
                    prioritizeImages={prioritizeImages}
                />
            ) : (section.items ?? []).length === 1 ? (
                <SingleImage
                    section={section}
                    prioritizeImages={prioritizeImages}
                />
            ) : null}
        </SectionContainer>
    )
}
